import { useState } from 'react';

import { Button } from '../Button';
import { Modal } from '../Modal';
import styles from './ConfirmDialog.module.css';

export type ConfirmDialogProps = {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly danger?: boolean;
  /** Дополнительное подтверждение: пользователь вводит строку (ключ проекта). */
  readonly confirmPhrase?: string;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  readonly isPending?: boolean;
  readonly error?: string | null;
};

/**
 * Деструктивные действия (docs/06 §4). При `confirmPhrase` кнопка активируется
 * только после точного ввода — двойное подтверждение удаления проекта.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Удалить',
  cancelLabel = 'Отмена',
  danger = true,
  confirmPhrase,
  onConfirm,
  onClose,
  isPending = false,
  error,
}: ConfirmDialogProps) {
  const [phrase, setPhrase] = useState('');
  const isPhraseValid = confirmPhrase === undefined || phrase.trim() === confirmPhrase;

  const handleClose = (): void => {
    setPhrase('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={isPending}
            disabled={!isPhraseValid}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description ? <p className={styles['description']}>{description}</p> : null}
      {confirmPhrase === undefined ? null : (
        <label className={styles['phraseField']}>
          <span>
            Введите <strong>{confirmPhrase}</strong> для подтверждения
          </span>
          <input
            value={phrase}
            aria-label="Строка подтверждения"
            onChange={(event) => setPhrase(event.target.value)}
          />
        </label>
      )}
      {error ? (
        <p className={styles['error']} role="alert">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}
